import { memo, useCallback, useMemo, useState } from "react";
import { ActionIcon, Box, Loader, Menu } from "@mantine/core";
import {
  IconChevronRight,
  IconDots,
  IconDownload,
  IconEdit,
  IconEye,
  IconFolder,
  IconFolderPlus,
  IconLayoutGrid,
  IconList,
  IconSearch,
  IconTrash,
  IconUpload,
} from "@tabler/icons-react";
import moment from "moment";
import FileGlyph from "./FileGlyph";
import FilePreview from "./FilePreview";
import { fileTypeOf } from "./fileTypes";
import { formatFileSize } from "../../utils";
import useStyles from "./style";

const idOf = (item) => item?._id || item?.id;

// Folders seeded by the platform (the default set created with every provider)
// are tagged rather than presented as something the user made.
const isSystemFolder = (folder) => !!(folder?.system || folder?.isSystem || folder?.isDefault);

// The nightly cron flips `expired`, but a document whose date has just passed
// should read as expired straight away rather than after the next run.
const expiryState = (file, showExpiry) => {
  if (!showExpiry || !file?.expiry) return null;
  const past = !!file?.expired || moment(file.expiry).endOf("day").isBefore(moment());
  return { past, date: moment(file.expiry).format("MM/DD/YYYY") };
};

const folderDetails = (folder) => {
  const files = folder?.files?.length || 0;
  const subFolders = folder?.folders?.length || 0;
  const parts = [`${files} file${files === 1 ? "" : "s"}`];
  if (subFolders) parts.push(`${subFolders} folder${subFolders === 1 ? "" : "s"}`);
  return parts.join(" · ");
};

const fileDetails = (file) => {
  const parts = [];
  if (file?.fileSize) parts.push(formatFileSize(file.fileSize));
  if (file?.createdAt) parts.push(moment(file.createdAt).format("MMM D, YYYY"));
  return parts.join(" · ") || "—";
};

/* -------------------------------------------------------------------------- */
/* Row pieces                                                                  */
/*                                                                             */
/* These live at module scope rather than inside DocumentsDrive on purpose. A  */
/* component declared in a render body is a brand new type on every render, so */
/* React remounts it — which would throw away each card's decoded preview and  */
/* close any open menu every time something as small as the selection changed. */
/* -------------------------------------------------------------------------- */

/**
 * Open / Download / Rename / divider / Delete, identical on folder chips, file
 * cards and list rows. Staff see Open and nothing else.
 */
const RowMenu = ({ classes, item, canManage, onOpen, onDownload, onRename, onDelete }) => (
  // The kebab sits inside a clickable card, so its own clicks must not also
  // select or open the item behind it.
  <Box
    className={classes.kebab}
    onClick={(event) => event.stopPropagation()}
    onDoubleClick={(event) => event.stopPropagation()}
    style={{ display: "inline-flex" }}
  >
    <Menu shadow="md" position="bottom-end" zIndex={1000} width={180}>
      <Menu.Target>
        <ActionIcon size={26} aria-label="Actions">
          <IconDots size={15} />
        </ActionIcon>
      </Menu.Target>
      <Menu.Dropdown>
        <Menu.Item icon={<IconEye size={15} />} onClick={() => onOpen?.(item)}>
          Open
        </Menu.Item>
        {canManage && (
          <>
            <Menu.Item icon={<IconDownload size={15} />} onClick={() => onDownload?.(item)}>
              Download
            </Menu.Item>
            <Menu.Item icon={<IconEdit size={15} />} onClick={() => onRename?.(item)}>
              Rename
            </Menu.Item>
            <Menu.Divider />
            <Menu.Item icon={<IconTrash size={15} />} color="red" onClick={() => onDelete?.(item)}>
              Delete
            </Menu.Item>
          </>
        )}
      </Menu.Dropdown>
    </Menu>
  </Box>
);

const FolderChip = memo(({ classes, cx, folder, selected, canManage, onSelect, ...actions }) => (
  <Box
    className={cx(classes.folderChip, { [classes.selected]: selected })}
    onClick={() => onSelect(idOf(folder))}
    onDoubleClick={() => actions.onOpen?.(folder)}
    title={folder?.name}
  >
    <span className={classes.folderChipIcon}>
      <IconFolder size={16} fill="currentColor" stroke={0} />
    </span>
    <span className={classes.folderChipName}>{folder?.name}</span>
    {isSystemFolder(folder) && <span className={classes.folderChipTag}>System</span>}
    <RowMenu classes={classes} item={folder} canManage={canManage} {...actions} />
  </Box>
));

const FileCard = memo(
  ({ classes, cx, file, selected, canManage, showExpiry, onSelect, ...actions }) => {
    const expiry = expiryState(file, showExpiry);
    return (
      <Box
        className={cx(classes.fileCard, { [classes.selected]: selected })}
        onClick={() => onSelect(idOf(file))}
        onDoubleClick={() => actions.onOpen?.(file)}
      >
        <Box className={classes.fileCardHead}>
          <FileGlyph file={file} size={20} />
          <span className={classes.fileCardName} title={file?.name}>
            {file?.name}
          </span>
          <RowMenu classes={classes} item={file} canManage={canManage} {...actions} />
        </Box>
        <FilePreview file={file} />
        <Box className={classes.fileCardMeta}>
          {file?.createdAt ? moment(file.createdAt).format("MMM D, YYYY") : "—"}
          {expiry && (
            <>
              {" · "}
              <span className={expiry.past ? classes.expiryPast : classes.expiry}>
                {expiry.past ? "Expired" : `Expires ${expiry.date}`}
              </span>
            </>
          )}
        </Box>
      </Box>
    );
  }
);

const FolderRow = memo(({ classes, cx, folder, selected, canManage, onSelect, ...actions }) => (
  <Box
    className={cx(classes.listRow, { [classes.selected]: selected })}
    onClick={() => onSelect(idOf(folder))}
    onDoubleClick={() => actions.onOpen?.(folder)}
  >
    <span className={classes.listFolderIcon}>
      <IconFolder size={17} fill="currentColor" stroke={0} />
    </span>
    <span className={classes.colName} title={folder?.name}>
      {folder?.name}
    </span>
    <span className={classes.colType}>{isSystemFolder(folder) ? "System folder" : "Folder"}</span>
    <span className={classes.colDetails}>{folderDetails(folder)}</span>
    {/* Folders never expire — the cell only exists to keep the columns lined up. */}
    <span className={classes.colExpiry}>—</span>
    <span className={classes.colMenu}>
      <RowMenu classes={classes} item={folder} canManage={canManage} {...actions} />
    </span>
  </Box>
));

const FileRow = memo(
  ({ classes, cx, file, selected, canManage, showExpiry, onSelect, ...actions }) => {
    const expiry = expiryState(file, showExpiry);
    return (
      <Box
        className={cx(classes.listRow, { [classes.selected]: selected })}
        onClick={() => onSelect(idOf(file))}
        onDoubleClick={() => actions.onOpen?.(file)}
      >
        <FileGlyph file={file} size={30} />
        <span className={classes.colName} title={file?.name}>
          {file?.name}
        </span>
        <span className={classes.colType}>{fileTypeOf(file).typeLabel}</span>
        <span className={classes.colDetails}>{fileDetails(file)}</span>
        <span className={classes.colExpiry}>
          {expiry ? (
            <span className={expiry.past ? classes.expiryPast : classes.expiry}>
              {expiry.past ? "Expired" : expiry.date}
            </span>
          ) : (
            "—"
          )}
        </span>
        <span className={classes.colMenu}>
          <RowMenu classes={classes} item={file} canManage={canManage} {...actions} />
        </span>
      </Box>
    );
  }
);

/* -------------------------------------------------------------------------- */

/**
 * The Documents module drive: a breadcrumb trail over one folder's contents,
 * shown either as a grid of folder chips and previewing file cards or as a
 * detail list. Both views render the same data, so switching between them
 * never refetches.
 *
 * The component is deliberately free of any provider/staff/client specifics —
 * it takes a folder path plus its contents and reports interactions back — so
 * the staff, client and facility document tabs can adopt it as they migrate off
 * the deprecated components/Document pattern.
 *
 * @param {Object}    props
 * @param {String}    props.rootName    Label for the root crumb (e.g. the provider's name).
 * @param {Object[]}  props.path        Folders opened below the root, outermost first.
 * @param {Object[]}  props.folders     Subfolders of the folder being shown.
 * @param {Object[]}  props.files       Files in the folder being shown.
 * @param {Boolean}   props.loading     Whether the contents are still being fetched.
 * @param {String}    props.search      Current search term (controlled).
 * @param {Boolean}   props.canManage   False for staff — hides New folder, Upload, and every
 *                                      action but Open.
 * @param {Boolean}   props.showExpiry  False when the caller's settings turn expiry tracking
 *                                      off; expiry then stops being flagged anywhere.
 * @param {Function}  props.onSearchChange
 * @param {Function}  props.onNavigate  Breadcrumb click; called with the depth to return to (0 = root).
 * @param {Function}  props.onOpenFolder
 * @param {Function}  props.onOpenFile
 * @param {Function}  props.onNewFolder
 * @param {Function}  props.onUpload
 * @param {Function}  props.onDownloadFolder / onRenameFolder / onDeleteFolder
 * @param {Function}  props.onDownloadFile / onRenameFile / onDeleteFile
 * @param {Node}      props.emptyState  Rendered inside the card body when nothing matches.
 */
const DocumentsDrive = ({
  rootName = "Documents",
  path = [],
  folders = [],
  files = [],
  loading = false,
  search = "",
  canManage = true,
  showExpiry = true,
  onSearchChange,
  onNavigate,
  onOpenFolder,
  onOpenFile,
  onNewFolder,
  onUpload,
  onDownloadFolder,
  onRenameFolder,
  onDeleteFolder,
  onDownloadFile,
  onRenameFile,
  onDeleteFile,
  emptyState,
}) => {
  const { classes, cx } = useStyles();
  const [view, setView] = useState("grid"); // grid | list
  const [selectedId, setSelectedId] = useState(null);

  // Stable so that selecting one item doesn't re-render every other card, and
  // in particular doesn't disturb previews that have already decoded.
  const onSelect = useCallback((id) => setSelectedId(id), []);

  const crumbs = useMemo(
    () => [rootName, ...path.map((folder) => folder?.name)],
    [rootName, path]
  );

  const folderActions = {
    onOpen: onOpenFolder,
    onDownload: onDownloadFolder,
    onRename: onRenameFolder,
    onDelete: onDeleteFolder,
  };
  const fileActions = {
    onOpen: onOpenFile,
    onDownload: onDownloadFile,
    onRename: onRenameFile,
    onDelete: onDeleteFile,
  };
  const shared = { classes, cx, canManage, onSelect };

  const isEmpty = folders.length === 0 && files.length === 0;

  const listing =
    view === "grid" ? (
      <Box className={classes.grid}>
        {folders.length > 0 && <Box className={classes.section}>Folders</Box>}
        {folders.map((folder) => (
          <FolderChip
            key={idOf(folder)}
            folder={folder}
            selected={selectedId === idOf(folder)}
            {...shared}
            {...folderActions}
          />
        ))}
        {files.length > 0 && <Box className={classes.section}>Files</Box>}
        {files.map((file) => (
          <FileCard
            key={idOf(file)}
            file={file}
            selected={selectedId === idOf(file)}
            showExpiry={showExpiry}
            {...shared}
            {...fileActions}
          />
        ))}
      </Box>
    ) : (
      <Box className={classes.list}>
        <Box className={classes.listHead}>
          <span className={classes.colName}>Name</span>
          <span className={classes.colType}>Type</span>
          <span className={classes.colDetails}>Details</span>
          <span className={classes.colExpiry}>Expiration</span>
          <span className={classes.colMenu} />
        </Box>
        {/* Folders always sort above files, matching the grid's section order. */}
        {folders.map((folder) => (
          <FolderRow
            key={idOf(folder)}
            folder={folder}
            selected={selectedId === idOf(folder)}
            {...shared}
            {...folderActions}
          />
        ))}
        {files.map((file) => (
          <FileRow
            key={idOf(file)}
            file={file}
            selected={selectedId === idOf(file)}
            showExpiry={showExpiry}
            {...shared}
            {...fileActions}
          />
        ))}
      </Box>
    );

  return (
    <Box className={classes.wrapper}>
      <Box className={classes.toolbar}>
        <Box className={classes.search}>
          <IconSearch size={15} stroke={2} />
          <input
            placeholder="Search files & folders"
            value={search}
            onChange={(event) => onSearchChange?.(event.target.value)}
          />
        </Box>
        {canManage && (
          <>
            <button type="button" className={classes.btnOutline} onClick={() => onNewFolder?.()}>
              <IconFolderPlus size={16} stroke={2} color="#FFAF4A" />
              New folder
            </button>
            <button type="button" className={classes.uploadBtn} onClick={() => onUpload?.()}>
              <IconUpload size={15} stroke={2.2} />
              Upload files
            </button>
          </>
        )}
      </Box>

      <Box className={classes.card}>
        <Box className={classes.cardHead}>
          <Box className={classes.crumbs}>
            {crumbs.map((name, index) =>
              index === crumbs.length - 1 ? (
                <span key={index} className={classes.crumbCurrent} title={name}>
                  {name}
                </span>
              ) : (
                <Box key={index} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                  <button
                    type="button"
                    className={classes.crumbLink}
                    title={name}
                    onClick={() => onNavigate?.(index)}
                  >
                    {name}
                  </button>
                  <span className={classes.crumbSep}>
                    <IconChevronRight size={14} stroke={2} />
                  </span>
                </Box>
              )
            )}
          </Box>

          <Box className={classes.viewToggle}>
            <button
              type="button"
              title="Grid"
              aria-label="Grid view"
              aria-pressed={view === "grid"}
              className={cx(classes.viewToggleBtn, { [classes.viewToggleBtnOn]: view === "grid" })}
              onClick={() => setView("grid")}
            >
              <IconLayoutGrid size={14} stroke={2} />
            </button>
            <button
              type="button"
              title="List"
              aria-label="List view"
              aria-pressed={view === "list"}
              className={cx(classes.viewToggleBtn, { [classes.viewToggleBtnOn]: view === "list" })}
              onClick={() => setView("list")}
            >
              <IconList size={14} stroke={2} />
            </button>
          </Box>
        </Box>

        <Box className={classes.cardBody}>
          {loading ? (
            <Box className={classes.loaderWrap}>
              <Loader size="sm" />
            </Box>
          ) : isEmpty ? (
            emptyState
          ) : (
            listing
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default DocumentsDrive;
